import PropTypes from '~/lib/propTypes'
import { color, fontSize, fontWeight, space } from '@ticketswap/solar'
import styled from '@emotion/styled'
import FacebookAuthButton from '~/components/Authentication/FacebookAuthButton'
import AppleAuthButton from '~/components/Authentication/AppleAuthButton'
import EmailAuthForm from '~/components/Authentication/EmailAuthForm'
import LastLoginMethod from '~/components/Authentication/LastLoginMethod'
import GoogleAuthButton from '~/components/Authentication/GoogleAuthButton'
import TermsAndConditionsLabel from '~/components/Authentication/TermsAndConditionsLabel'
import { getEnabledFeatures } from '~/utils/beta'
import useFeatureFlag from '~/lib/optimizely/useFeatureFlag'

const Divider = styled.p`
  color: ${color.foreground};
  font-size: ${fontSize[18]};
  font-weight: ${fontWeight.semiBold};
  display: flex;
  flex-basis: 100%;
  align-items: center;
  margin-block: ${space[16]};
  margin-inline: 0;
  justify-content: center;

  &::after,
  &::before {
    content: '';
    flex-grow: 1;
    background: ${color.stroke};
    height: 1px;
    font-size: 0;
    line-height: 0;
  }

  &:before {
    margin-block: 0;
    margin-inline: ${space[16]} 0;
  }

  &:after {
    margin-block: 0;
    margin-inline: 0 ${space[16]};
  }
`

const SocialButtons = styled.div`
  display: grid;
  grid-gap: ${space[8]};
`

const Login = ({
  t,
  redirectUrl,
  onEmailLogin,
  onEmailSignup,
  onSocialLogin,
  onSocialSignup,
  onLoginWithCode,
  onSignupWithCode,
}) => {
  const { isFeatureFlagEnabled } = useFeatureFlag({
    featureKey: 'login_with_code',
  })
  const enabledFeatures = getEnabledFeatures({})
  const isLoginWithCode = enabledFeatures.includes('LOGIN_WITH_CODE') || isFeatureFlagEnabled

  return (
    <>
      <LastLoginMethod t={t} />

      <SocialButtons>
        <GoogleAuthButton
          onLogin={() => onSocialLogin('google')}
          onSignup={user => onSocialSignup(user, 'google')}
        >
          {t('common:auth.login.buttons.google')}
        </GoogleAuthButton>

        <FacebookAuthButton
          t={t}
          onLogin={() => onSocialLogin('facebook')}
          onSignup={user => onSocialSignup(user, 'facebook')}
        >
          {t('common:auth.login.buttons.facebook')}
        </FacebookAuthButton>

        <AppleAuthButton
          t={t}
          onLogin={() => onSocialLogin('apple')}
          onSignup={user => onSocialSignup(user, 'apple')}
        >
          {t('common:auth.login.buttons.apple')}
        </AppleAuthButton>
      </SocialButtons>

      <Divider>{t('common:auth.login.divider.text')}</Divider>

      <EmailAuthForm
        t={t}
        method={isLoginWithCode ? 'code' : 'email'}
        redirectUrl={redirectUrl}
        onEmailLogin={onEmailLogin}
        onEmailSignup={onEmailSignup}
        onLoginWithCode={onLoginWithCode}
        onSignupWithCode={onSignupWithCode}
      />

      <TermsAndConditionsLabel t={t} />
    </>
  )
}

Login.propTypes = {
  t: PropTypes.func.isRequired,
  redirectUrl: PropTypes.string,
  onEmailLogin: PropTypes.func.isRequired,
  onEmailSignup: PropTypes.func.isRequired,
  onSocialLogin: PropTypes.func.isRequired,
  onSocialSignup: PropTypes.func.isRequired,
  onLoginWithCode: PropTypes.func.isRequired,
}

export default Login
